import { useState, useEffect } from "react";

import { makeStyles } from "@mui/styles";
import { Typography, Stack, Divider, Box } from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineSeparator,
  TimelineItem,
} from "@mui/lab";

import useAxios from "axios-hooks";

import Loader from "components/loader";
import MainCard from "components/mainCard";
import { OrderTimelineSkeleton } from "components/skeleton/orderDetailSkeleton";

import {
  convertMinutesInToHours,
  getLocalStorage,
} from "features/components/helperFunctions";
import ReservationStatusActionButton from "./reservationStatusActionButton";
import { ReservationTimelineResponseResultTimeline } from "utils/types";
import { API_END_POINTS } from "utils/constants/APIEndPoints";
import { dispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";
import { ReservationStatusActionProps } from "features/reservations/index.types";

const useStyles = makeStyles(() => ({
  TimeLineRoot: {
    marginTop: "30px !important",
    marginBottom: "unset",
    padding: "unset !important",
    marginLeft: "8px !important",

    "& li:nth-last-child(1) span": {
      backgroundColor: "unset !important",
    },
    "& li:nth-last-child(2) span": {
      marginBottom: "3px !important",
    },
  },

  TimelineItem: {
    "&::before": {
      padding: "0px",
      postion: "unset",
      flex: "unset",
    },
  },

  TimelineDot: {
    background: " #fff",
    display: "block",
    width: "14px",
    height: "14px",
    position: "absolute",
    margin: "unset !important",
    padding: "unset !important",
    transform: "translate(-50%, -50%)",
    border: "4px solid #DB154D",
  },

  TimelineDotInActive: {
    background: " #fff",
    display: "block",
    width: "14px",
    height: "14px",
    position: "absolute",
    margin: "unset !important",
    padding: "unset !important",
    transform: "translate(-50%, -50%)",
    border: "4px solid #757575",
  },

  TimeLineConnector: {},
  TimeLineContent: {
    paddingLeft: "12px !important",
    marginTop: "-16px",
    marginBottom: "16px",
    paddingRight: "unset !important",
  },
}));

const ReservationStatusAction = ({
  reservationId,
  reservationComment,
  reservationStatusActionCallback,
}: ReservationStatusActionProps) => {
  const [reservationTimeline, setReservationTimeline] = useState<
    ReservationTimelineResponseResultTimeline[]
  >([]);

  const { eatout_id, user_id } = getLocalStorage();
  const classes = useStyles();

  //======================================= API Call Payloads =======================================//

  const getReservationTimelineAPIPayload = () => {
    const formData = new FormData();

    formData.append("type", "res");
    formData.append("reservation_id", reservationId);

    formData.append("eatout_id", `${eatout_id}`);
    formData.append("admin_id", `${user_id}`);
    formData.append("source", "biz");
    return formData;
  };

  //======================================= API Calls =======================================//

  // get reservation timeline
  const [
    {
      data: getReservationTimeline,
      loading: getReservationTimelineLoading,
      error: getReservationTimelineError,
    },
    getReservationTimelineAPICall,
  ] = useAxios(API_END_POINTS.getReservationsTimeline, { manual: true });

  //======================================= useEffect Hooks =======================================//

  useEffect(() => {
    // get reservation timeline api call
    callGetReservationTimelineAPI();
  }, []);

  useEffect(() => {
    if (getReservationTimelineError) {
      // notify user
      dispatch(
        openSnackbar({
          open: true,
          message: getReservationTimelineError
            ? getReservationTimelineError.message
            : "",
          alert: {
            color: "error",
          },
        })
      );
    }
  }, [getReservationTimelineError]);

  //======================================= Handler Functions =======================================//

  // calling get reservation timeline api
  const callGetReservationTimelineAPI = async () => {
    const { data } = await getReservationTimelineAPICall({
      data: getReservationTimelineAPIPayload(),
    });

    // status 1 => getting required response from API
    if (data?.status === "1") {
      setReservationTimeline(data?.result?.timeline);
    }
  };

  const reservationStatusActionButtonCallback = () => {
    callGetReservationTimelineAPI();

    // callback to call getReservation apo
    reservationStatusActionCallback();
  };

  return (
    <>
      {getReservationTimelineLoading && <Loader />}

      {!getReservationTimeline ? (
        <Box
          sx={{
            p: "0px 16px 24px 16px",
            border: "1px solid #EEEEEE",
            mb: "24px",
          }}
        >
          <OrderTimelineSkeleton />
        </Box>
      ) : (
        // reservation status timeline card
        <MainCard
          dividerSX={{ m: "0px 0px 0px 0px !important" }}
          headerSX={{ p: "unset !important", mb: "24px" }}
          contentSX={{
            "& .MuiCardContent-root": {
              p: "unset !important",
            },
            m: "unset",
            p: "unset !important",
          }}
          sx={{
            m: "0px 0px 16px",
            p: "24px 16px 0px 16px",
            borderColor: "#EEEEEE",
          }}
          title={
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography variant="h4">Reservation Status</Typography>
            </Stack>
          }
        >
          <Timeline
            sx={{
              "& .ul.MuiTimeline-root.MuiTimeline-positionRight.css-dkyxik-MuiTimeline-root li:last-child span":
                {
                  backgroundColor: "unset !important",
                },
            }}
            className={classes.TimeLineRoot}
          >
            {reservationTimeline?.map(
              (
                timeline: ReservationTimelineResponseResultTimeline,
                timelineIndex: number
              ) => (
                <TimelineItem
                  className={classes.TimelineItem}
                  key={timelineIndex}
                >
                  <TimelineSeparator sx={{ display: "flex", direction: "row" }}>
                    <TimelineDot
                      color="secondary"
                      className={
                        timeline.date
                          ? classes.TimelineDot
                          : classes.TimelineDotInActive
                      }
                    />
                    <TimelineConnector className={classes.TimeLineConnector} />
                  </TimelineSeparator>
                  <TimelineContent className={classes.TimeLineContent}>
                    <Stack
                      direction={"row"}
                      justifyContent="space-between"
                      display={"flex"}
                    >
                      {timeline.date ? (
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ alignItems: "center" }}
                          >
                            <Typography variant="subtitle1">
                              {timeline.date}
                            </Typography>
                            <Typography variant="h5">
                              {timeline.time}
                            </Typography>
                          </Stack>

                          <Typography variant="body2">
                            {convertMinutesInToHours(timeline.minutes_passed)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack>
                          <Typography variant="subtitle1">
                            Awaiting status
                          </Typography>
                        </Stack>
                      )}
                      <Stack>
                        <ReservationStatusActionButton
                          reservationId={reservationId}
                          timeline={timeline}
                          reservationStatusActionButtonCallback={
                            reservationStatusActionButtonCallback
                          }
                        />
                      </Stack>
                    </Stack>
                  </TimelineContent>
                </TimelineItem>
              )
            )}
          </Timeline>

          {reservationComment && (
            <>
              <Divider sx={{ border: "1px solid #EEEEEE", mb: "16px" }} />
              <Box sx={{ ml: "26px", mr: "26px", mb: "24px" }}>
                <Typography variant={"h5"} sx={{ mb: "6px" }}>
                  Reason/Comment:
                </Typography>
                <Typography variant={"body1"}>{reservationComment}</Typography>
              </Box>
            </>
          )}
        </MainCard>
      )}
    </>
  );
};

export default ReservationStatusAction;
